import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import {
  appendProntuarioVersion,
  buildSnapshotFromMedicalRecord,
  ensureProntuarioForRecord,
} from "@/lib/prontuario/versioning";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";

type MedicalRecord = Database["public"]["Tables"]["medical_records"]["Row"];
type MedicalRecordInsert = Database["public"]["Tables"]["medical_records"]["Insert"];
type MedicalRecordUpdate = Database["public"]["Tables"]["medical_records"]["Update"];

type PetWeightSource = "manual" | "registration" | "profile_update" | "consultation";

type PetWeightConsultationUpsert = Database["public"]["Tables"]["pet_weights"]["Insert"] & {
  medical_record_id: string;
  source: PetWeightSource;
};

const getUserAgent = () =>
  typeof window !== "undefined" ? window.navigator.userAgent : null;

const requireCRMV = (crmvState?: string | null, crmvNumber?: number | null) => {
  if (!crmvState || crmvState.trim() === "" || !crmvNumber || crmvNumber <= 0) {
    throw new Error("CRMV é obrigatório para salvar o prontuário.");
  }
};

const syncConsultationWeight = async (record: MedicalRecord) => {
  const weightValue = typeof record.weight === "number" ? record.weight : null;
  const measuredAt = record.consultation_date ?? null;

  if (!record.id || !record.pet_id) return;

  if (!weightValue) {
    // Soft-delete any active linked weight entry.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("pet_weights")
        .update({ deleted_at: new Date().toISOString() })
        .eq("medical_record_id", record.id)
        .is("deleted_at", null);
      if (error) throw error;
    } catch (error) {
      // Backwards-compatible fallback: if the DB hasn't been migrated yet (no medical_record_id),
      // we can't reliably delete the linked entry. Ignore.
      console.warn("Falha ao remover peso vinculado à consulta (fallback):", error);
    }
    return;
  }

  // Preferred path: store provenance + link to the medical record.
  // If the migration isn't applied yet, fall back to a plain insert (still updates "peso atual").
  try {
    const payload: PetWeightConsultationUpsert = {
      pet_id: record.pet_id,
      weight: weightValue,
      measured_at: measuredAt,
      deleted_at: null,
      medical_record_id: record.id,
      source: "consultation",
    };

    // Can't use ON CONFLICT here because the unique index is partial
    // (medical_record_id WHERE deleted_at IS NULL), which PostgREST upsert can't target.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: updatedRows, error: updateError } = await (supabase as any)
      .from("pet_weights")
      .update({
        weight: weightValue,
        measured_at: measuredAt,
        source: "consultation",
        deleted_at: null,
      })
      .eq("medical_record_id", record.id)
      .is("deleted_at", null)
      .select("id");

    if (updateError) throw updateError;
    if (Array.isArray(updatedRows) && updatedRows.length > 0) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: insertError } = await (supabase as any).from("pet_weights").insert(payload);
    if (!insertError) return;

    const errorCode =
      typeof insertError === "object" && insertError !== null && "code" in insertError
        ? String(insertError.code)
        : null;

    if (errorCode === "23505") {
      // Race condition: another request inserted first. Update the active linked row.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: retryUpdateError } = await (supabase as any)
        .from("pet_weights")
        .update({
          weight: weightValue,
          measured_at: measuredAt,
          source: "consultation",
          deleted_at: null,
        })
        .eq("medical_record_id", record.id)
        .is("deleted_at", null);

      if (retryUpdateError) throw retryUpdateError;
      return;
    }

    throw insertError;
  } catch (error) {
    console.warn("Falha ao sincronizar peso com metadados (tentando fallback):", error);
  }

  // Fallback: insert without the new columns.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: fallbackError } = await (supabase as any).from("pet_weights").insert({
    pet_id: record.pet_id,
    weight: weightValue,
    measured_at: measuredAt,
  });
  if (fallbackError) throw fallbackError;
};

export const useMedicalRecords = (petId?: string, appointmentId?: string) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: profile } = useProfile();

  // Fetch medical records
  const { data: medicalRecords, isLoading } = useQuery({
    queryKey: ["medical-records", petId, appointmentId],
    queryFn: async () => {
      let query = supabase
        .from("medical_records")
        .select(`
          *,
          pets (
            id,
            name,
            owners (
              id,
              name
            )
          ),
          profiles!fk_vet_user (
            id,
            full_name
          ),
          clinics (
            id,
            name
          )
        `)
        .is("deleted_at", null)
        .order("consultation_date", { ascending: false });

      if (petId) {
        query = query.eq("pet_id", petId);
      }

      if (appointmentId) {
        query = query.eq("appointment_id", appointmentId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as MedicalRecord[];
    },
    enabled: !!petId || !!appointmentId,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Create medical record
  const createMedicalRecord = useMutation({
    mutationFn: async (record: Omit<MedicalRecordInsert, 'vet_user_id'>) => {
      if (!user) throw new Error("Usuário não autenticado");

      requireCRMV(profile?.crmv_state ?? null, profile?.crmv_number ?? null);

      const recordData: MedicalRecordInsert = {
        ...record,
        vet_user_id: user.id,
        clinic_id: record.clinic_id || profile?.current_clinic_id || '',
      };
      // O trigger populate_medical_record_crmv_mapa preenche crmv e mapa_number automaticamente

      const { data, error } = await supabase
        .from("medical_records")
        .insert(recordData)
        .select()
        .single();

      if (error) throw error;

      // Criar prontuário base e primeira versão
      const prontuario = await ensureProntuarioForRecord(data, user.id);
      await appendProntuarioVersion({
        prontuarioId: prontuario.id,
        autorId: user.id,
        snapshot: buildSnapshotFromMedicalRecord(data),
        acao: "CREATE",
        tipo: "NORMAL",
        userAgent: getUserAgent(),
      });

      return data;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["medical-records"] });
      invalidatePatientsCache(queryClient, { petIds: created?.pet_id ? [created.pet_id] : null });
      toast.success("Consulta criada com sucesso");

      if (created) {
        void syncConsultationWeight(created)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["pet", created.pet_id] });
          })
          .catch((error: unknown) => {
            console.warn("Falha ao sincronizar peso da consulta:", error);
            toast.error("Consulta salva, mas falhou ao atualizar histórico de peso.");
          });
      }
    },
    onError: (error: Error) => {
      console.error("Erro ao criar consulta:", error);
      toast.error("Erro ao criar consulta");
    },
  });

  // Update medical record
  const updateMedicalRecord = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: MedicalRecordUpdate;
    }) => {
      if (!user) throw new Error("Usuário não autenticado");

      const { data: existing, error: fetchError } = await supabase
        .from("medical_records")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      requireCRMV(profile?.crmv_state ?? null, profile?.crmv_number ?? null);

      const prontuario = await ensureProntuarioForRecord(
        existing,
        user.id,
      );

      const updatePayload = {
        ...data,
      } as MedicalRecordUpdate;
      // O trigger atualiza crmv e mapa_number quando vet_user_id muda

      const { data: result, error } = await supabase
        .from("medical_records")
        .update(updatePayload)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      await appendProntuarioVersion({
        prontuarioId: prontuario.id,
        autorId: user.id,
        snapshot: buildSnapshotFromMedicalRecord(result),
        acao: "UPDATE",
        tipo: "NORMAL",
        userAgent: getUserAgent(),
      });

      return result;
    },
    onSuccess: (updated, variables) => {
      queryClient.invalidateQueries({ queryKey: ["medical-records"] });
      queryClient.invalidateQueries({ queryKey: ["medical-record", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["prontuario-timeline", variables.id] });
      invalidatePatientsCache(queryClient, { petIds: updated?.pet_id ? [updated.pet_id] : null });
      toast.success("Consulta atualizada com sucesso");

      if (updated) {
        void syncConsultationWeight(updated)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["pet", updated.pet_id] });
          })
          .catch((error: unknown) => {
            console.warn("Falha ao sincronizar peso da consulta:", error);
            toast.error("Consulta salva, mas falhou ao atualizar histórico de peso.");
          });
      }
    },
    onError: (error: Error) => {
      console.error("Erro ao atualizar consulta:", error);
      toast.error("Erro ao atualizar consulta");
    },
  });

  // Soft delete medical record
  const deleteMedicalRecord = useMutation({
    onMutate: async (id: string) => {
      const { data, error } = await supabase
        .from("medical_records")
        .select("pet_id")
        .eq("id", id)
        .single();
      if (error) throw error;
      return { petId: data?.pet_id ?? null };
    },
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("medical_records")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: (_data, _variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["medical-records"] });
      invalidatePatientsCache(queryClient, { petIds: context?.petId ? [context.petId] : null });
      toast.success("Consulta excluída com sucesso");

      if (context?.petId && _variables) {
        const recordId = _variables;
        // Best-effort: remove linked weight entry.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        void (supabase as any)
          .from("pet_weights")
          .update({ deleted_at: new Date().toISOString() })
          .eq("medical_record_id", recordId)
          .is("deleted_at", null)
          .then(({ error }: { error?: unknown }) => {
            if (error) throw error;
            queryClient.invalidateQueries({ queryKey: ["pet", context.petId] });
          })
          .catch((error: unknown) => {
            console.warn("Falha ao sincronizar remoção do peso da consulta:", error);
          });
      }
    },
    onError: (error: Error) => {
      console.error("Erro ao excluir consulta:", error);
      toast.error("Erro ao excluir consulta");
    },
  });

  return {
    medicalRecords,
    isLoading,
    createMedicalRecord: createMedicalRecord.mutateAsync,
    updateMedicalRecord: updateMedicalRecord.mutateAsync,
    deleteMedicalRecord: deleteMedicalRecord.mutateAsync,
  };
};
